import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FcCollapse,
  FcBusinessman,
  FcSettings,
  FcDocument,
  FcKey,
  FcCancel,
  FcPrevious,
  FcServices,
  FcConferenceCall,
  FcSupport,
  FcSurvey,
  FcCalendar,
  FcOrganization,
  FcReading,
  FcSpeaker,
  FcFactory,
  FcCloseUpMode,
  FcRules,
  FcDepartment,
  FcAssistant,
  FcGraduationCap,
  FcStatistics,
  FcDataSheet,
  FcFeedback
} from 'react-icons/fc';
import { FaLock } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useEstablishmentPermissions } from '../../hooks/useEstablishmentPermissions';
import { useEstablishment } from '../../contexts/EstablishmentContext';
import { Role } from '../../constants/roles';
import { NavItem } from '../../constants/sidebar';
import logo from '../../assets/logo.png';
import { UnclassifiedIntakeBadge } from './UnclassifiedIntakeBadge';

// Static icon map - defined outside component to avoid recreation on every render
const ICON_MAP: Record<string, React.ReactNode> = {
  // Icon field mappings (from backend)
  'LayoutDashboard': <FcStatistics className="w-5 h-5" />,
  'UserPlus': <FcBusinessman className="w-5 h-5" />,
  'AlertCircle': <FcCancel className="w-5 h-5" />,
  'AlertTriangle': <FcSupport className="w-5 h-5" />,
  'Support': <FcSupport className="w-5 h-5" />,
  'Wrench': <FcServices className="w-5 h-5" />,
  'Services': <FcServices className="w-5 h-5" />,
  'Survey': <FcSurvey className="w-5 h-5" />,
  'Reading': <FcReading className="w-5 h-5" />,
  'MessageSquare': <FcSpeaker className="w-5 h-5" />,
  'Calendar': <FcCloseUpMode className="w-5 h-5" />,
  'CalendarAlt': <FcCalendar className="w-5 h-5" />,
  'CalendarCheck': <FcCalendar className="w-5 h-5" />,
  'FileText': <FcRules className="w-5 h-5" />,
  'Users': <FcConferenceCall className="w-5 h-5" />,
  'CalendarDays': <FcDataSheet className="w-5 h-5" />,
  'Mail': <FcSpeaker className="w-5 h-5" />,
  'Building': <FcOrganization className="w-5 h-5" />,
  'Building2': <FcDepartment className="w-5 h-5" />,
  'DoorOpen': <FcKey className="w-5 h-5" />,
  'GraduationCap': <FcGraduationCap className="w-5 h-5" />,
  'Briefcase': <FcAssistant className="w-5 h-5" />,
  'HardHat': <FcFactory className="w-5 h-5" />,
  'HeartHandshake': <FcAssistant className="w-5 h-5" />,
  'Shield': <FaLock className="w-5 h-5 text-yellow-500" />,
  'RiskMap': <FcDataSheet className="w-5 h-5" />,
  'Settings': <FcSettings className="w-5 h-5" />,
  'Inbox': <FcFeedback className="w-5 h-5" />,
  // Code-based mappings (legacy)
  '0': <FcStatistics className="w-5 h-5" />,
  '1': <FcServices className="w-5 h-5" />,
  '2': <FcConferenceCall className="w-5 h-5" />,
  '3': <FaLock className="w-5 h-5 text-yellow-500" />,
  '4': <FcSupport className="w-5 h-5" />,
  '5': <FcOrganization className="w-5 h-5" />,
  '6': <FcSpeaker className="w-5 h-5" />,
  '7': <FcSurvey className="w-5 h-5" />,
  '8': <FcCalendar className="w-5 h-5" />,
  '9': <FcRules className="w-5 h-5" />,
  '10': <FcSettings className="w-5 h-5" />,
  '11': <FcKey className="w-5 h-5" />,
  '12': <FcDepartment className="w-5 h-5" />,
  '13': <FcOrganization className="w-5 h-5" />,
  '14': <FcSupport className="w-5 h-5" />,
  '15': <FcGraduationCap className="w-5 h-5" />,
  '16': <FcStatistics className="w-5 h-5" />,
  '17': <FcBusinessman className="w-5 h-5" />,
  '19': <FcFeedback className="w-5 h-5" />,
};

const DEFAULT_ICON = <FcDocument className="w-5 h-5" />;

const RESIDENT_GOVERNANCE_PATH = '/governanca/condomino';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  showLogoSection?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, showLogoSection = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { logout, navigation, isLoading: authLoading } = useAuth();
  const {
    hasAnyPermission,
    hasRole,
  } = useEstablishmentPermissions();
  const { currentEstablishment } = useEstablishment();

  const navItems = useMemo(() => {
    return navigation || [];
  }, [navigation]);

  const [openCollapse, setOpenCollapse] = useState<Record<string, boolean>>({});

  const keyOf = (item: NavItem): string =>
    item.id || item.code || item.path || item.urlPath || item.title || item.name || '';

  // Resolve the active navigation branch by TREE ancestry, choosing the nav item
  // whose path is the LONGEST match for the current location. This decouples URL
  // nesting from the menu tree: e.g. "Mapa de Risco" lives at a URL nested under
  // "Manutenção Preventiva/Condomínio" but is a separate top-level entry — its
  // longer, exact path wins over the shorter-prefix parent, so that parent no
  // longer lights up or expands when Mapa de Risco is open.
  const activeChainKeys = useMemo(() => {
    const normalizePath = (value: string): string =>
      value.endsWith('/') ? value.slice(0, -1) : value;
    const current = normalizePath(location.pathname);

    let bestKeys: string[] = [];
    let bestLen = -1;
    const walk = (items: NavItem[], ancestors: string[]): void => {
      items.forEach((item) => {
        const chain = [...ancestors, keyOf(item)];
        if (item.children && item.children.length > 0) {
          walk(item.children, chain);
        }
        const rawPath = item.path || item.urlPath;
        if (rawPath) {
          const path = normalizePath(rawPath);
          if (current === path || current.startsWith(`${path}/`)) {
            if (path.length > bestLen) {
              bestKeys = chain;
              bestLen = path.length;
            }
          }
        }
      });
    };
    walk(navItems, []);

    return new Set<string>(bestKeys);
  }, [location.pathname, navItems]);

  const isActive = (item: NavItem): boolean => activeChainKeys.has(keyOf(item));

  // Seed the accordion so only the active branch is open on load/navigation.
  // Depends solely on the memoized activeChainKeys (stable until the route or the
  // navigation tree changes), so manual toggles are never clobbered on re-render.
  useEffect(() => {
    const seeded: Record<string, boolean> = {};
    activeChainKeys.forEach((key) => {
      seeded[key] = true;
    });
    setOpenCollapse(seeded);
  }, [activeChainKeys]);

  const getItemIcon = (item: NavItem) => {
    if (item.icon && ICON_MAP[item.icon]) {
      return ICON_MAP[item.icon];
    }
    if (item.code) {
      const topLevelCode = item.code.split('.')[0];
      if (topLevelCode && ICON_MAP[topLevelCode]) {
        return ICON_MAP[topLevelCode];
      }
    }
    return DEFAULT_ICON;
  };

  const handleCollapseToggle = (itemId: string, level: number = 1, siblingIds: readonly string[] = []) => {
    setOpenCollapse((prev) => {
      if (level === 1) {
        const isCurrentlyOpen = prev[itemId];
        if (isCurrentlyOpen) {
          return {};
        }
        return { [itemId]: true };
      }

      const newState = { ...prev };

      siblingIds.forEach((siblingId) => {
        if (siblingId !== itemId) {
          delete newState[siblingId];
        }
      });

      const nextValue = !prev[itemId];
      if (nextValue) {
        newState[itemId] = true;
      } else {
        delete newState[itemId];
      }

      return newState;
    });
  };

  const hasItemPermission = (item: NavItem): boolean => {
    // Backend already filters navigation by permissions, so if an item is returned, user has permission
    // This is just a safety check for any additional frontend filtering if needed

    // Flag do síndico (por estabelecimento corrente): esconde a Governança do
    // morador quando showGovernanceToResidents === false. O nó chega do backend
    // para todo morador; a flag é aplicada aqui, no client.
    const itemPathForFlag = item.path || item.urlPath;
    if (
      itemPathForFlag === RESIDENT_GOVERNANCE_PATH &&
      hasRole(Role.Resident) &&
      currentEstablishment?.showGovernanceToResidents === false
    ) {
      return false;
    }

    // If item has specific permissions, check them against establishment permissions
    if (item.permissions && item.permissions.length > 0) {
      // Check if user has any of the required permissions in current establishment
      return hasAnyPermission(item.permissions);
    }
    
    // Check path-based permissions
    const path = item.path || item.urlPath;
    if (path) {
      const pathSegments = path.split('/').filter(Boolean);
      const possiblePermissions = [
        pathSegments.join('.') + '.view',
        pathSegments.join('.') + '.access',
        pathSegments[0] + '.view',
        pathSegments[0] + '.access',
      ];
      
      if (hasAnyPermission(possiblePermissions)) {
        return true;
      }
    }
    
    // If backend sent the item and no specific permissions required, user has permission to see it
    return true;
  };

  const renderNavItems = (items: NavItem[], sidebarIsOpen: boolean, level: number = 1, parentKey: string = 'root'): React.ReactNode => {
    const visibleItems = items
      .filter((item) => hasItemPermission(item))
      .map((item, index) => {
        const baseKey = item.id || item.code || item.path || item.urlPath || item.title || item.name || ''
        const fallbackKey = `${parentKey}-${level}-${index}`
        return {
          item,
          itemKey: baseKey || fallbackKey,
        }
      });

    const siblingKeys = visibleItems.map(({ itemKey }) => itemKey);

    return visibleItems.map(({ item, itemKey }) => {
        const currentItemIsActive = isActive(item);
        const hasVisibleChildren = item.children && item.children.some(child => hasItemPermission(child));

        if (hasVisibleChildren) {
          // Check if any child is active to highlight the parent
          const isParentActiveDueToChild = item.children!.some(child => 
            isActive(child) || (child.children && child.children.some(grandchild => isActive(grandchild)))
          );
          // Determine if the collapse should be open.
          // Strict accordion: rely solely on `openCollapse` state (seeded with the
          // active section by initializeAccordionState). Do NOT fall back to the
          // active route here, otherwise the active section stays "stuck" open and
          // coexists with any newly opened section, breaking single-open behavior.
          const isCollapseOpen = openCollapse[itemKey] === true;

          const baseClasses = `group flex items-center w-full text-sm font-medium rounded-xl transition-[background-color,color,transform,box-shadow] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm`;
          const paddingClasses = level === 1
            ? (sidebarIsOpen ? 'px-4 py-2' : 'p-2 justify-center')
            : level === 2 ? 'px-6 py-1.5' : 'px-8 py-1';
          const levelClasses = currentItemIsActive || isParentActiveDueToChild
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform scale-[1.02] hover:scale-[1.03]'
            : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:text-blue-700 hover:shadow-md hover:scale-[1.01] border border-gray-100';
          
          const itemPath = item.path || item.urlPath;

          return (
            <div key={itemKey} className="mb-1">
              <div className={`${baseClasses} ${paddingClasses} ${levelClasses}`}>
                {itemPath ? (
                  <Link
                    to={itemPath}
                    className="flex items-center flex-1 min-w-0"
                  >
                    {level === 1 && (
                      <span className={`${sidebarIsOpen ? 'mr-3' : ''} flex-shrink-0`}>
                        {getItemIcon(item)}
                      </span>
                    )}
                    <span className={`flex-1 text-left font-medium ${!sidebarIsOpen ? 'sr-only' : ''}`}>
                      {item.title || item.name}
                    </span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="flex items-center flex-1 min-w-0"
                    onClick={() => handleCollapseToggle(itemKey, level, siblingKeys)}
                  >
                    {level === 1 && (
                      <span className={`${sidebarIsOpen ? 'mr-3' : ''} flex-shrink-0`}>
                        {getItemIcon(item)}
                      </span>
                    )}
                    <span className={`flex-1 text-left font-medium ${!sidebarIsOpen ? 'sr-only' : ''}`}>
                      {item.title || item.name}
                    </span>
                  </button>
                )}
                {sidebarIsOpen && (
                  <button
                    type="button"
                    onClick={() => handleCollapseToggle(itemKey, level, siblingKeys)}
                    className="ml-2 p-1 rounded hover:bg-black/5 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] flex-shrink-0"
                    aria-label={isCollapseOpen ? 'Recolher' : 'Expandir'}
                  >
                    <span className={`block transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isCollapseOpen ? 'rotate-180' : 'rotate-0'}`}>
                      <FcCollapse className="w-4 h-4" />
                    </span>
                  </button>
                )}
              </div>
              <div
                className={`overflow-hidden transition-[max-height,opacity,margin] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                  isCollapseOpen && sidebarIsOpen
                    ? 'max-h-[2000px] opacity-100 mt-1'
                    : 'max-h-0 opacity-0 mt-0'
                }`}
              >
                <div className={`space-y-1 ${level === 1 ? 'pl-2' : level === 2 ? 'pl-4' : 'pl-6'}`}>
                  {renderNavItems(item.children!, sidebarIsOpen, level + 1, itemKey)}
                </div>
              </div>
            </div>
          );
        }

        // Leaf node (no children or no visible children)
        const paddingClasses = level === 1
          ? (sidebarIsOpen ? 'px-4 py-2' : 'p-2 justify-center')
          : level === 2 ? 'px-6 py-1.5' : 'px-8 py-1';
        const leafClasses = `group flex items-center text-sm font-medium rounded-xl transition-[background-color,color,transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm mb-1 ${paddingClasses} ${
          currentItemIsActive
            ? level === 1
              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform scale-[1.02] hover:scale-[1.03]'
              : 'bg-blue-50 text-blue-700 border-l-4 border-blue-500 hover:bg-blue-100'
            : level === 1
              ? 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 hover:text-blue-700 hover:shadow-md hover:scale-[1.01] border border-gray-100'
              : 'bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 border-l-4 border-transparent hover:border-blue-300 hover:translate-x-1'
        }`;

        return (
          <Link
            key={itemKey}
            to={item.path || item.urlPath || '#'}
            className={leafClasses}
          >
            {level === 1 && (
              <span className={`${sidebarIsOpen ? 'mr-3' : ''} flex-shrink-0`}>
                {getItemIcon(item)}
              </span>
            )}
            <span className={`font-medium ${!sidebarIsOpen ? 'sr-only' : ''}`}>
              {item.title || item.name}
            </span>
            {(item.path || item.urlPath) === '/central-de-solicitacoes' && sidebarIsOpen && (
              <UnclassifiedIntakeBadge enabled={hasAnyPermission(['intake-requests.view'])} />
            )}
            {currentItemIsActive && level === 1 && sidebarIsOpen && (
              <span className="ml-auto h-2 w-2 bg-white rounded-full opacity-75"></span>
            )}
          </Link>
        );
      });
  };

  return (
    <div className={`${isOpen ? 'w-72' : 'w-16'} h-full bg-gradient-to-b from-gray-50 to-gray-100 border-r border-gray-200 flex flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 shadow-lg transition-[width] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]`}>
      {showLogoSection && (
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            {isOpen && (
              <img src={logo} alt="Logo" data-no-outline className="h-8 w-auto transition-opacity duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] animate-in fade-in zoom-in" />
            )}
            <button
              onClick={onToggle}
              aria-label="Toggle sidebar"
              className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-1.5 hover:bg-gray-100 hover:scale-110 transition-[background-color,color,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
            >
              {isOpen ? <FcPrevious className="w-6 h-6" /> : <FcCollapse className="w-6 h-6" />}
            </button>
          </div>
        </div>
      )}

      <nav className="flex-1 px-2 py-4 space-y-1">
        {authLoading && (
          <>
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className={`animate-pulse ${isOpen ? 'h-9' : 'h-8'} bg-white border border-gray-100 rounded-xl ${isOpen ? 'px-4' : 'px-2'} ${isOpen ? 'py-2' : 'py-2'} mb-1`} />
            ))}
          </>
        )}
        {!authLoading && renderNavItems(navItems, isOpen)}
      </nav>

      <div className="p-4 border-t border-gray-200 bg-white">
        <button
          onClick={logout}
          className={`w-full group flex items-center text-sm font-medium rounded-xl px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 hover:scale-[1.02] transition-[background-color,color,transform,box-shadow] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm hover:shadow-md ${
            !isOpen ? 'justify-center' : ''
          }`}
        >
          <FcCancel className={`${isOpen ? 'mr-3' : ''} w-5 h-5 flex-shrink-0 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:rotate-12`} />
          {isOpen && <span className="font-medium">{t('common.logout')}</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
